// =============================================================================
// FacilityManagement.jsx - Multi-Facility Settings Component
// =============================================================================
// Manages club facilities including:
// - View all facilities
// - Add new facilities (within subscription tier limits)
// - Edit facility name/address
// - Set primary facility
// - Delete facilities (only if no allocations exist)
// - MIGRATE legacy club data to facility document
// =============================================================================

import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where, setDoc, updateDoc, deleteDoc, increment } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import PremiumFeatureModal from '../PremiumFeatureModal';
import TrialExpiredModal from '../TrialExpiredModal';

// =============================================================================
// FACILITY LIMITS PER SUBSCRIPTION TIER
// =============================================================================
const FACILITY_LIMITS = {
  free: 1,
  basic: 1,
  standard: 2,
  premium: 5
};

// =============================================================================
// FACILITY MANAGEMENT COMPONENT
// =============================================================================
const FacilityManagement = ({
  clubInfo,
  showSuccess,
  isAdmin = true,
  subscriptionTier = 'basic'
}) => {
  // ===========================================================================
  // STATE
  // ===========================================================================
  const [isExpanded, setIsExpanded] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Track primaryFacilityId locally (props may not update immediately)
  const [localPrimaryFacilityId, setLocalPrimaryFacilityId] = useState(null);
  
  // Sync local primary ID with props
  useEffect(() => {
    if (clubInfo?.primaryFacilityId && !localPrimaryFacilityId) {
      setLocalPrimaryFacilityId(clubInfo.primaryFacilityId);
    }
  }, [clubInfo?.primaryFacilityId, localPrimaryFacilityId]);
  
  // Use local state for primary, fallback to props
  const primaryFacilityId = localPrimaryFacilityId || clubInfo?.primaryFacilityId;
  const [selectedFacility, setSelectedFacility] = useState(null);
  
  // Form states
  const [facilityName, setFacilityName] = useState('');
  const [facilityAddress, setFacilityAddress] = useState('');
  
  // Paywall states
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  
  // ===========================================================================
  // LEGACY MIGRATION STATE
  // ===========================================================================
  const [hasLegacyData, setHasLegacyData] = useState(false);
  const [legacyData, setLegacyData] = useState(null);
  const [isMigrating, setIsMigrating] = useState(false);
  
  // ===========================================================================
  // COMPUTED VALUES
  // ===========================================================================
  const facilityLimit = FACILITY_LIMITS[subscriptionTier] || 1;
  const canAddFacility = facilities.length < facilityLimit;
  
  // ===========================================================================
  // LOAD FACILITIES (with legacy detection)
  // ===========================================================================
  useEffect(() => {
    const loadFacilities = async () => {
      if (!clubInfo?.clubId) {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        console.log('📦 [FacilityManagement] Loading facilities for club:', clubInfo.clubId);
        
        const facilitiesRef = collection(db, 'clubs', clubInfo.clubId, 'facilities');
        const facilitiesSnap = await getDocs(facilitiesRef);
        
        const facilitiesData = [];
        facilitiesSnap.forEach((doc) => {
          facilitiesData.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort: primary first, then alphabetically by name
        facilitiesData.sort((a, b) => {
          if (a.isPrimary || a.id === primaryFacilityId) return -1;
          if (b.isPrimary || b.id === primaryFacilityId) return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
        
        setFacilities(facilitiesData);
        console.log('✅ [FacilityManagement] Loaded facilities:', facilitiesData.length);
        
        // ===========================================================================
        // SYNC FACILITY COUNT (fix any mismatches)
        // ===========================================================================
        const clubRef = doc(db, 'clubs', clubInfo.clubId);
        const clubDoc = await getDoc(clubRef);
        if (clubDoc.exists()) {
          const currentStoredCount = clubDoc.data().facilityCount || 0;
          if (currentStoredCount !== facilitiesData.length) {
            console.log(`🔄 [FacilityManagement] Syncing facilityCount: ${currentStoredCount} → ${facilitiesData.length}`);
            await updateDoc(clubRef, {
              facilityCount: facilitiesData.length
            });
          }
        }
        
        // ===========================================================================
        // CHECK FOR LEGACY DATA (if no facilities exist)
        // ===========================================================================
        if (facilitiesData.length === 0) {
          console.log('🔍 [FacilityManagement] No facilities found, checking for legacy data...');
          
          const clubRef = doc(db, 'clubs', clubInfo.clubId);
          const clubDoc = await getDoc(clubRef);
          
          if (clubDoc.exists()) {
            const clubData = clubDoc.data();
            
            // Check if club has legacy facility data
            const hasLegacy = !!(
              clubData.facilityConfigured || 
              clubData.satelliteImageUrl || 
              clubData.satelliteImage ||
              (clubData.pitches && clubData.pitches.length > 0)
            );
            
            if (hasLegacy) {
              console.log('📦 [FacilityManagement] Legacy data detected:', {
                facilityConfigured: clubData.facilityConfigured,
                hasSatelliteImage: !!(clubData.satelliteImageUrl || clubData.satelliteImage),
                pitchCount: clubData.pitches?.length || 0
              });
              
              setHasLegacyData(true);
              setLegacyData({
                name: clubData.clubName || clubInfo.name || 'Main Facility',
                satelliteImageUrl: clubData.satelliteImageUrl || clubData.satelliteImage,
                pitches: clubData.pitches || [],
                facilityConfigured: clubData.facilityConfigured,
                satelliteConfig: clubData.satelliteConfig,
                imageWidth: clubData.imageWidth,
                imageHeight: clubData.imageHeight,
                pitchSectionConfigurations: clubData.pitchSectionConfigurations,
                pitchReferencePoints: clubData.pitchReferencePoints,
                pitchNames: clubData.pitchNames
              });
            }
          }
        } else {
          // Facilities exist, clear any legacy state
          setHasLegacyData(false);
          setLegacyData(null);
        }
      } catch (err) {
        console.error('❌ [FacilityManagement] Error loading facilities:', err);
        setError('Failed to load facilities');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (isExpanded) {
      loadFacilities();
    }
  }, [clubInfo?.clubId, isExpanded, primaryFacilityId]);
  
  // ===========================================================================
  // MIGRATE LEGACY DATA
  // ===========================================================================
  const handleMigrateLegacy = async () => {
    if (!legacyData || !clubInfo?.clubId) return;
    
    try {
      setIsMigrating(true);
      setError(null);
      
      const facilityId = `facility-${Date.now()}`;
      const now = new Date().toISOString();
      
      // Check if there's already a primary facility
      const clubRef = doc(db, 'clubs', clubInfo.clubId);
      const clubDoc = await getDoc(clubRef);
      const existingPrimaryId = clubDoc.exists() ? clubDoc.data().primaryFacilityId : null;
      const shouldBePrimary = !existingPrimaryId;
      
      console.log('🔄 [FacilityManagement] Migrating legacy data to facility:', facilityId);
      console.log('🔄 [FacilityManagement] Will be primary:', shouldBePrimary);
      
      // Create facility document from legacy data
      const facilityData = {
        id: facilityId,
        name: legacyData.name,
        address: '',
        isPrimary: shouldBePrimary,
        isConfigured: legacyData.facilityConfigured || false,
        isShared: false,
        pitchCount: legacyData.pitches?.length || 0,
        pitches: legacyData.pitches || [],
        satelliteImageUrl: legacyData.satelliteImageUrl || null,
        satelliteImage: legacyData.satelliteImageUrl || null,
        satelliteConfig: legacyData.satelliteConfig || null,
        imageWidth: legacyData.imageWidth || null,
        imageHeight: legacyData.imageHeight || null,
        pitchSectionConfigurations: legacyData.pitchSectionConfigurations || null,
        pitchReferencePoints: legacyData.pitchReferencePoints || null,
        pitchNames: legacyData.pitchNames || null,
        createdAt: now,
        updatedAt: now,
        migratedFromLegacy: true,
        migratedAt: now
      };
      
      // Save facility document
      const facilityRef = doc(db, 'clubs', clubInfo.clubId, 'facilities', facilityId);
      await setDoc(facilityRef, facilityData);
      
      // Update club document - only set primaryFacilityId if not already set
      const clubUpdateData = {
        facilityCount: increment(1),
        updatedAt: now
      };
      
      // Only set as primary if no primary exists yet
      if (shouldBePrimary) {
        clubUpdateData.primaryFacilityId = facilityId;
      }
      
      await updateDoc(clubRef, clubUpdateData);
      
      console.log('✅ [FacilityManagement] Legacy data migrated successfully');
      
      // Update local state
      setFacilities([facilityData]);
      setHasLegacyData(false);
      setLegacyData(null);
      
      // Update local primary facility ID state
      if (shouldBePrimary) {
        setLocalPrimaryFacilityId(facilityId);
        console.log('✅ [FacilityManagement] Set as primary facility');
      }
      
      showSuccess('Facility migrated successfully!');
      
    } catch (err) {
      console.error('❌ [FacilityManagement] Migration error:', err);
      setError('Failed to migrate facility data');
    } finally {
      setIsMigrating(false);
    }
  };
  
  // ===========================================================================
  // TRIAL CHECK
  // ===========================================================================
  const isTrialExpired = () => {
    if (!clubInfo) return false;
    
    const now = new Date();
    const trialEndsAt = clubInfo.trialEndsAt?.toDate ? 
      clubInfo.trialEndsAt.toDate() : 
      new Date(clubInfo.trialEndsAt);
    
    const subscriptionStatus = clubInfo.subscriptionStatus || 'trial';
    const msRemaining = trialEndsAt - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    
    return subscriptionStatus === 'trial' && daysRemaining <= 0;
  };
  
  // ===========================================================================
  // HANDLERS
  // ===========================================================================
  const handleExpandToggle = () => {
    if (isTrialExpired()) {
      setShowTrialExpiredModal(true);
      return;
    }
    setIsExpanded(!isExpanded);
  };
  
  const handleAddClick = () => {
    if (!canAddFacility) {
      setShowPaywallModal(true);
      return;
    }
    setFacilityName('');
    setFacilityAddress('');
    setError(null);
    setShowAddModal(true);
  };
  
  const handleEditClick = (facility) => {
    setSelectedFacility(facility);
    setFacilityName(facility.name || '');
    setFacilityAddress(facility.address || '');
    setError(null);
    setShowEditModal(true);
  };
  
  const handleDeleteClick = async (facility) => {
    // Check if this is the primary and only facility
    if (facilities.length === 1) {
      setError('Cannot delete your only facility.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    // Check for allocations before allowing delete
    try {
      setIsSaving(true);
      const allocationsRef = collection(db, 'clubs', clubInfo.clubId, 'allocations');
      const allocationsQuery = query(allocationsRef, where('facilityId', '==', facility.id));
      const allocationsSnap = await getDocs(allocationsQuery);
      
      if (!allocationsSnap.empty) {
        setError(`Cannot delete "${facility.name}" - it has ${allocationsSnap.size} allocation(s). Delete or move allocations first.`);
        setTimeout(() => setError(null), 5000);
        return;
      }
      
      setSelectedFacility(facility);
      setShowDeleteConfirm(true);
    } catch (err) {
      console.error('❌ [FacilityManagement] Error checking allocations:', err);
      setError('Failed to check allocations');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSetPrimary = async (facility) => {
    if (facility.isPrimary || facility.id === primaryFacilityId) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      const clubRef = doc(db, 'clubs', clubInfo.clubId);
      await updateDoc(clubRef, {
        primaryFacilityId: facility.id,
        updatedAt: new Date().toISOString()
      });
      
      // Update all facilities' isPrimary flag
      const batch = [];
      for (const f of facilities) {
        const facilityRef = doc(db, 'clubs', clubInfo.clubId, 'facilities', f.id);
        batch.push(updateDoc(facilityRef, {
          isPrimary: f.id === facility.id,
          updatedAt: new Date().toISOString()
        }));
      }
      await Promise.all(batch);
      
      // Update local state
      setFacilities(prev => {
        const updated = prev.map(f => ({
          ...f,
          isPrimary: f.id === facility.id
        }));
        // Re-sort with new primary first
        return updated.sort((a, b) => {
          if (a.id === facility.id) return -1;
          if (b.id === facility.id) return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
      });
      
      // Update local primary facility ID state
      setLocalPrimaryFacilityId(facility.id);
      
      console.log('✅ [FacilityManagement] Primary facility updated to:', facility.id);
      showSuccess(`${facility.name} is now your primary facility`);
    } catch (err) {
      console.error('❌ [FacilityManagement] Error setting primary facility:', err);
      setError('Failed to update primary facility');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleAddFacility = async () => {
    if (!facilityName.trim()) {
      setError('Facility name is required');
      return;
    }
    
    try {
      setIsSaving(true);
      setError(null);
      
      const facilityId = `facility-${Date.now()}`;
      const now = new Date().toISOString();
      const isFirstFacility = facilities.length === 0;
      
      const facilityData = {
        id: facilityId,
        name: facilityName.trim(),
        address: facilityAddress.trim() || '',
        isPrimary: isFirstFacility,
        isConfigured: false,
        isShared: false,
        pitchCount: 0,
        pitches: [],
        createdAt: now,
        updatedAt: now
      };
      
      // Save facility document
      const facilityRef = doc(db, 'clubs', clubInfo.clubId, 'facilities', facilityId);
      await setDoc(facilityRef, facilityData);
      
      // Update club document
      const clubRef = doc(db, 'clubs', clubInfo.clubId);
      const updateData = {
        facilityCount: increment(1),
        updatedAt: now
      };
      
      if (isFirstFacility) {
        updateData.primaryFacilityId = facilityId;
      }
      
      await updateDoc(clubRef, updateData);
      
      // Update local primary facility ID state
      if (isFirstFacility) {
        setLocalPrimaryFacilityId(facilityId);
      }
      
      console.log('✅ [FacilityManagement] Facility created:', facilityId);
      if (isFirstFacility) {
        console.log('✅ [FacilityManagement] Set as primary facility');
      }
      
      // Update local state
      setFacilities(prev => {
        const updated = [...prev, facilityData];
        return updated.sort((a, b) => {
          if (a.isPrimary) return -1;
          if (b.isPrimary) return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
      });
      
      setShowAddModal(false);
      showSuccess(`${facilityName} added successfully`);
    } catch (err) {
      console.error('❌ [FacilityManagement] Error creating facility:', err);
      setError('Failed to create facility');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleUpdateFacility = async () => {
    if (!facilityName.trim() || !selectedFacility) {
      setError('Facility name is required');
      return;
    }
    
    try {
      setIsSaving(true);
      setError(null);
      
      const facilityRef = doc(db, 'clubs', clubInfo.clubId, 'facilities', selectedFacility.id);
      await updateDoc(facilityRef, {
        name: facilityName.trim(),
        address: facilityAddress.trim() || '',
        updatedAt: new Date().toISOString()
      });
      
      console.log('✅ [FacilityManagement] Facility updated:', selectedFacility.id);
      
      // Update local state
      setFacilities(prev => prev.map(f => 
        f.id === selectedFacility.id 
          ? { ...f, name: facilityName.trim(), address: facilityAddress.trim() || '' }
          : f
      ));
      
      setShowEditModal(false);
      setSelectedFacility(null);
      showSuccess('Facility updated successfully');
    } catch (err) {
      console.error('❌ [FacilityManagement] Error updating facility:', err);
      setError('Failed to update facility');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeleteFacility = async () => {
    if (!selectedFacility) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      const wasPrimary = selectedFacility.isPrimary || selectedFacility.id === primaryFacilityId;
      
      // Delete facility document
      const facilityRef = doc(db, 'clubs', clubInfo.clubId, 'facilities', selectedFacility.id);
      await deleteDoc(facilityRef);
      
      // Update club document
      const clubRef = doc(db, 'clubs', clubInfo.clubId);
      const updateData = {
        facilityCount: increment(-1),
        updatedAt: new Date().toISOString()
      };
      
      // If this was the primary, assign new primary
      if (wasPrimary && facilities.length > 1) {
        const newPrimary = facilities.find(f => f.id !== selectedFacility.id);
        if (newPrimary) {
          updateData.primaryFacilityId = newPrimary.id;
          
          // Update new primary facility's isPrimary flag
          const newPrimaryRef = doc(db, 'clubs', clubInfo.clubId, 'facilities', newPrimary.id);
          await updateDoc(newPrimaryRef, {
            isPrimary: true,
            updatedAt: new Date().toISOString()
          });
        }
      }
      
      await updateDoc(clubRef, updateData);
      
      console.log('✅ [FacilityManagement] Facility deleted:', selectedFacility.id);
      
      // Update local state
      setFacilities(prev => {
        const remaining = prev.filter(f => f.id !== selectedFacility.id);
        if (wasPrimary && remaining.length > 0) {
          remaining[0].isPrimary = true;
        }
        return remaining;
      });
      
      // Update local primary facility ID if primary was deleted
      if (wasPrimary && facilities.length > 1) {
        const newPrimary = facilities.find(f => f.id !== selectedFacility.id);
        if (newPrimary) {
          setLocalPrimaryFacilityId(newPrimary.id);
        }
      } else if (wasPrimary && facilities.length === 1) {
        setLocalPrimaryFacilityId(null);
      }
      
      setShowDeleteConfirm(false);
      setSelectedFacility(null);
      showSuccess('Facility deleted successfully');
    } catch (err) {
      console.error('❌ [FacilityManagement] Error deleting facility:', err);
      setError('Failed to delete facility');
    } finally {
      setIsSaving(false);
    }
  };
  
  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (
    <>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {/* =================================================================
            DROPDOWN HEADER
            ================================================================= */}
        <div
          onClick={handleExpandToggle}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '8px',
            margin: '-8px',
            borderRadius: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
              Facilities
            </h2>
            <span style={{
              padding: '4px 10px',
              backgroundColor: hasLegacyData ? '#fef3c7' : '#f3f4f6',
              color: hasLegacyData ? '#92400e' : '#6b7280',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: '500'
            }}>
              {hasLegacyData ? '⚠️ Migration needed' : `🏟️ ${facilities.length} / ${facilityLimit}`}
            </span>
          </div>
          <div style={{
            fontSize: '20px',
            color: '#6b7280',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </div>
        </div>
        
        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '12px', marginBottom: 0 }}>
          Manage your club's facilities and venues
        </p>
        
        {/* =================================================================
            EXPANDED CONTENT
            ================================================================= */}
        {isExpanded && (
          <div style={{ marginTop: '24px' }}>
            {/* Error message */}
            {error && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            {/* Loading state */}
            {isLoading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                color: '#6b7280'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid #e5e7eb',
                  borderTopColor: '#3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '12px'
                }} />
                Loading facilities...
              </div>
            ) : (
              <>
                {/* =============================================================
                    LEGACY MIGRATION PROMPT
                    ============================================================= */}
                {hasLegacyData && legacyData && (
                  <div style={{
                    padding: '20px',
                    backgroundColor: '#fef3c7',
                    border: '2px solid #f59e0b',
                    borderRadius: '12px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ fontSize: '32px' }}>🏟️</div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
                          Existing Facility Detected
                        </h3>
                        <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#78350f' }}>
                          Your club has existing facility data that needs to be migrated to the new multi-facility system.
                        </p>
                        
                        <div style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          marginBottom: '16px'
                        }}>
                          <div style={{ fontSize: '14px', color: '#374151' }}>
                            <strong>Found:</strong>
                            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                              {legacyData.satelliteImageUrl && <li>Satellite image configured</li>}
                              {legacyData.pitches?.length > 0 && <li>{legacyData.pitches.length} pitch(es) configured</li>}
                              {legacyData.facilityConfigured && <li>Facility setup complete</li>}
                            </ul>
                          </div>
                        </div>
                        
                        <button
                          onClick={handleMigrateLegacy}
                          disabled={isMigrating}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: isMigrating ? '#9ca3af' : '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: isMigrating ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          {isMigrating ? (
                            <>
                              <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid white',
                                borderTopColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }} />
                              Migrating...
                            </>
                          ) : (
                            <>
                              🔄 Migrate to "{legacyData.name}"
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* =============================================================
                    FACILITIES LIST OR EMPTY STATE
                    ============================================================= */}
                {!hasLegacyData && facilities.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#6b7280'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏟️</div>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                      No facilities set up yet
                    </p>
                    <p style={{ fontSize: '14px', marginBottom: '20px' }}>
                      Add your first facility to start managing pitches and allocations.
                    </p>
                    {isAdmin && (
                      <button
                        onClick={handleAddClick}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        + Add First Facility
                      </button>
                    )}
                  </div>
                ) : !hasLegacyData && (
                  <>
                    {/* Facilities list */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                      {facilities.map((facility) => {
                        const isPrimary = facility.id === primaryFacilityId;
                        
                        return (
                          <div
                            key={facility.id}
                            style={{
                              padding: '16px 20px',
                              backgroundColor: isPrimary ? '#f0fdf4' : '#f9fafb',
                              border: `2px solid ${isPrimary ? '#10b981' : '#e5e7eb'}`,
                              borderRadius: '10px'
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              flexWrap: 'wrap',
                              gap: '12px'
                            }}>
                              <div style={{ flex: 1, minWidth: '200px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                                    {facility.name}
                                  </h3>
                                  {isPrimary && (
                                    <span style={{
                                      padding: '2px 8px',
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      borderRadius: '10px',
                                      fontSize: '11px',
                                      fontWeight: '600'
                                    }}>
                                      PRIMARY
                                    </span>
                                  )}
                                </div>
                                
                                {facility.address && (
                                  <p style={{ margin: '4px 0 8px 0', fontSize: '13px', color: '#6b7280' }}>
                                    📍 {facility.address}
                                  </p>
                                )}
                                
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  {facility.pitchCount > 0 || facility.pitches?.length > 0 ? (
                                    <span style={{
                                      padding: '4px 10px',
                                      backgroundColor: '#dbeafe',
                                      color: '#1e40af',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '500'
                                    }}>
                                      {facility.pitchCount || facility.pitches?.length} pitch{(facility.pitchCount || facility.pitches?.length) !== 1 ? 'es' : ''}
                                    </span>
                                  ) : (
                                    <span style={{
                                      padding: '4px 10px',
                                      backgroundColor: '#fef3c7',
                                      color: '#92400e',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '500'
                                    }}>
                                      Not configured
                                    </span>
                                  )}
                                  
                                  {facility.isConfigured && (
                                    <span style={{
                                      padding: '4px 10px',
                                      backgroundColor: '#dcfce7',
                                      color: '#166534',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '500'
                                    }}>
                                      ✓ Configured
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {isAdmin && (
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  {!isPrimary && (
                                    <button
                                      onClick={() => handleSetPrimary(facility)}
                                      disabled={isSaving}
                                      style={{
                                        padding: '6px 12px',
                                        backgroundColor: 'white',
                                        color: '#10b981',
                                        border: '1px solid #10b981',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        cursor: isSaving ? 'not-allowed' : 'pointer',
                                        opacity: isSaving ? 0.6 : 1
                                      }}
                                    >
                                      ⭐ Set Primary
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleEditClick(facility)}
                                    disabled={isSaving}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: '#f3f4f6',
                                      color: '#374151',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      cursor: isSaving ? 'not-allowed' : 'pointer',
                                      opacity: isSaving ? 0.6 : 1
                                    }}
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(facility)}
                                    disabled={isSaving}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: '#fee2e2',
                                      color: '#dc2626',
                                      border: '1px solid #fecaca',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      cursor: isSaving ? 'not-allowed' : 'pointer',
                                      opacity: isSaving ? 0.6 : 1
                                    }}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Add button */}
                    {isAdmin && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px'
                      }}>
                        <button
                          onClick={handleAddClick}
                          disabled={isSaving}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: canAddFacility ? '#3b82f6' : '#9ca3af',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            opacity: isSaving ? 0.6 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {canAddFacility ? '+ Add Facility' : '🔒 Upgrade to Add More'}
                        </button>
                        
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>
                          {facilities.length} of {facilityLimit} facilities on {subscriptionTier} plan
                        </span>
                      </div>
                    )}
                    
                    {/* Info box */}
                    <div style={{
                      marginTop: '20px',
                      padding: '12px 16px',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#0369a1'
                    }}>
                      <strong>💡 Tip:</strong> If your club has two separate locations, then you can set up a secondary facility (location) here. 
                      The primary facility is shown by default in the allocator.
                      Once you've set up a new facility, you can add/edit pitches in the Pitch configuration menu.
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
      
      {/* =================================================================
          ADD/EDIT MODAL
          ================================================================= */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '480px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
              {showAddModal ? '🏟️ Add New Facility' : '✏️ Edit Facility'}
            </h3>
            
            {error && (
              <div style={{
                padding: '10px 14px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Facility Name <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                placeholder="e.g., Main Ground, Training Complex"
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Address <span style={{ color: '#6b7280', fontWeight: '400' }}>(Optional)</span>
              </label>
              <input
                type="text"
                value={facilityAddress}
                onChange={(e) => setFacilityAddress(e.target.value)}
                placeholder="e.g., 123 Sports Drive, Adelaide SA 5000"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setError(null);
                }}
                disabled={isSaving}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={showAddModal ? handleAddFacility : handleUpdateFacility}
                disabled={isSaving || !facilityName.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: isSaving || !facilityName.trim() ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isSaving || !facilityName.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                {isSaving ? 'Saving...' : (showAddModal ? 'Add Facility' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* =================================================================
          DELETE CONFIRMATION MODAL
          ================================================================= */}
      {showDeleteConfirm && selectedFacility && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚠️</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                Delete Facility?
              </h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                Are you sure you want to delete "<strong>{selectedFacility.name}</strong>"? 
                This action cannot be undone.
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isSaving}
                style={{
                  flex: 1,
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteFacility}
                disabled={isSaving}
                style={{
                  flex: 1,
                  padding: '10px 20px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.6 : 1
                }}
              >
                {isSaving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* =================================================================
          UPGRADE MODAL
          ================================================================= */}
      <PremiumFeatureModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        featureName="Additional Facilities"
        currentTier={subscriptionTier}
        requiredTiers={['standard', 'premium']}
        benefits={[
          "Manage multiple training grounds and venues",
          "Separate satellite images for each facility",
          "Independent pitch configurations per facility",
          "Dedicated share links for each location",
          "Standard: Up to 2 facilities",
          "Premium: Up to 5 facilities"
        ]}
        upgradeUrl="/pricing"
      />
      
      {/* =================================================================
          TRIAL EXPIRED MODAL
          ================================================================= */}
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
      />
      
      {/* Spin animation for loading state */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default FacilityManagement;
